import { TFilterValues } from '@/components/organisms/or-filtered-grid/or-filtered-grid.types';
import { TPage } from '@/lib/contentstack-sdk/types';
import { sendGTMEvent } from '@next/third-parties/google';
import { BlogPost } from 'contentstack/generated';
import { getCookie } from 'cookies-next';
import { sha256 } from 'js-sha256';

// Keep track of forms that have already fired the 'web_form_start' event
const formStartEventsFired = new Set<string>();

export const sendPageLoadGTMEvent = (
  pageType: string | string[] | undefined,
  articleData?: TPage,
) => {
  let slug;

  let articleValues: { [key: string]: string | undefined } = {
    article_title: undefined,
    article_topic: undefined,
    article_publish_date: undefined,
    article_author: undefined,
  };

  if (articleData?.url.includes('blog/')) {
    const blogPost = articleData as unknown as BlogPost;
    articleValues = {
      article_title: blogPost.title || undefined,
      article_topic: blogPost.category || undefined,
      article_publish_date: blogPost.date || undefined,
      article_author: blogPost.author || undefined,
    };
  }

  if (!pageType) {
    slug = 'home';
  } else {
    slug = Array.isArray(pageType) ? pageType[0] : pageType;
  }

  slug = slug.replace(/-/g, ' ');

  function hashEmail(email: string) {
    const normalizedEmail = email.toLowerCase();
    return sha256(normalizedEmail);
  }

  const rawUserState = getCookie('MySchumacherIsShared');

  let userState = null;
  if (rawUserState && typeof rawUserState === 'string') {
    try {
      if (typeof userState === 'string') {
        userState = JSON.parse(userState);
      }
      const decoded = decodeURIComponent(rawUserState);

      userState = JSON.parse(decoded);
    } catch (e) {
      console.error(
        'Failed to parse MySchumacherIsShared cookie:',
        rawUserState,
        e,
      );
    }
  }

  const basicData = {
    event: 'page_loaded',
    page: {
      page_type: slug,
      page_location: document.location.href,
      page_title: document.title,
      page_referrer: document.referrer,
      page_path: document.location.pathname,
      persona: '{{Persona}}', // To be updated with actual user data if applicable
    },
    user: {
      login_status: userState ? userState?.AuthenticationStatus === 1 : false,
      user_id: null,
      hashed_email: userState?.UserEmail
        ? hashEmail(userState?.UserEmail)
        : null,
      hashed_phone: '{{SHA256 Hashed phone number}}',
    },
    article: {
      ...articleValues,
    },
  };

  sendGTMEvent(basicData);
};

export const sendGTMNavigationEvent = (
  label: string,
  interactLocation: string,
  url: string,
) => {
  sendGTMEvent({
    event: 'nav_click',
    click_text: label,
    nav_placement: interactLocation,
    click_url: url,
  });
};

export const sendGTMClickEvent = (
  label: string,
  interactLocation: string,
  url: string,
) => {
  if (interactLocation !== 'test') {
    sendGTMEvent({
      event: 'cta_click',
      cta_name: label,
      cta_type: label,
      click_url: url,
      interaction_location: interactLocation,
    });
  }
};

export const sendZeroResultsGTMEvent = (
  searchTerm: string | TFilterValues,
  searchLocation: string,
) => {
  sendGTMEvent({
    event: 'zero_results',
    search_term: searchTerm,
    search_location: searchLocation,
  });
};

export const sendSelectLocationOnMapGTMEvent = (
  clickText: string,
  clickUrl: string,
) => {
  sendGTMEvent({
    event: 'select_location_click',
    click_text: clickText,
    click_url: clickUrl,
  });
};

export const sendGTMFilterEvent = (
  filterType: string,
  numOfResults: number,
  selectedOption: string,
) => {
  sendGTMEvent({
    event: 'filter_applied',
    filter_type: filterType,
    no_of_results: `${numOfResults}`,
    selected_option: selectedOption,
  });
};

export const sendChatGTMEvent = (
  action: string,
  interactionLocation: string,
  message?: string,
) => {
  sendGTMEvent({
    event: 'chat_interaction',
    chat_action: action,
    interaction_location: interactionLocation,
    message_sent: message,
  });
};

export const sendErrorGTMEvent = (type: 404 | 500): void => {
  sendGTMEvent({
    event: 'error',
    type,
  });
};

export const sendHomePlanClickGTMEvent = (
  modelName: string,
  modelId: number,
  url: string,
) => {
  sendGTMEvent({
    event: 'plan_click',
    click_url: url,
    details: { model_name: modelName, model_id: modelId },
  });
};

export const sendGTMFormStartEvent = (
  formName: string,
  formId: string,
  firstFieldName: string,
) => {
  // Check if the event has already been fired for this form
  if (formStartEventsFired.has(formId)) {
    return;
  }

  // Mark this form as having fired the event
  formStartEventsFired.add(formId);

  sendGTMEvent({
    event: 'web_form_start',
    form: {
      form_name: formName,
      form_id: formId,
      first_field: firstFieldName,
    },
  });
};

// Function to reset the formStartEventsFired set
export const resetFormStartEvents = () => {
  formStartEventsFired.clear();
};

export type TFormSubmitEvent = {
  form_name: string;
  form_id: string;
  step_no: number;
  closest_building_area: string;
  lot_ownership: string;
  hashed_email: string;
  hashed_phone: string;
  move_in_date: string;
  marketing_opt_in: boolean;
  contact_page_type_url?: string;
};

export const sendGTMFormSubmitEvent = (data: TFormSubmitEvent) => {
  const {
    form_name,
    form_id,
    step_no,
    closest_building_area,
    lot_ownership,
    hashed_email,
    hashed_phone,
    marketing_opt_in,
    move_in_date,
  } = data;

  sendGTMEvent({
    event: 'web_form_submit',
    form: {
      form_name,
      form_id,
      step_no,
    },
    form_field: {
      closest_building_area,
      lot_ownership,
      move_in_date,
      marketing_opt_in,
    },
    user: {
      hashed_email,
      hashed_phone,
    },
  });
};

export const sendGTMFormErrorEvent = (data: {
  form_name: string;
  form_field: string;
  error_message: string;
}) => {
  const { form_name, form_field, error_message } = data;

  sendGTMEvent({
    event: 'web_form_error',
    form_name,
    form_field,
    error_message,
  });
};
